import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { ApiError } from "@/api/types";
import { captureApiException } from "@/shared/lib/sentry";

/**
 * Central reporting for failed queries/mutations. Only server faults (5xx) and
 * unexpected/network errors reach Sentry — expected 4xx (auth, validation,
 * not-found) are routine and stay out. Deduped against the api-client layer so
 * the same error object is reported once.
 */
function reportQueryError(error: unknown): void {
  if (error instanceof ApiError) {
    if (error.status >= 500) {
      captureApiException(error, {
        tags: {
          status: error.status,
          component: "react-query",
          ...(error.requestId ? { request_id: error.requestId } : {}),
        },
      });
    }
    return;
  }
  // Non-ApiError => network drop or unexpected throw — always report.
  captureApiException(error, { tags: { component: "react-query" } });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reportQueryError }),
  mutationCache: new MutationCache({ onError: reportQueryError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      /**
       * Never poll a tab nobody is looking at.
       *
       * Fifteen hooks across the app set `refetchInterval` and only two
       * (inbox, notifications) guarded it, so every other one kept hitting the
       * API while the tab sat in the background — supplier-update at 10s,
       * packaging-tracker at 30s, shipments and the upcoming calendar at 60s,
       * four seafood hooks at 5m. An operator who leaves Ubik open in a
       * background tab all day was generating thousands of requests that
       * nothing rendered.
       *
       * A default rather than fifteen edits: the next hook to add polling gets
       * it for free, which is the failure mode that produced this state. React
       * Query still refetches on focus when the data is stale, so returning to
       * the tab shows current data — the poll is what stops, not the refresh.
       *
       * Set it to `true` on a specific query only for something that must
       * advance while hidden (a long render whose completion fires a toast).
       */
      refetchIntervalInBackground: false,
      /**
       * How long an unmounted query's data survives before it is dropped and
       * the next mount has to re-fetch from scratch.
       *
       * The default is 5 minutes, which is short for this app because the
       * workbench is tabbed: moving Ask → Inbox → Meetings → back unmounts each
       * page, and anything left for more than 5 minutes was being re-fetched on
       * return even though it was still perfectly current. 15 minutes keeps a
       * normal working session's worth of navigation served from memory.
       *
       * This is a cache-retention ceiling, not a freshness one — `staleTime`
       * still decides whether a mount revalidates. Retained data is shown
       * immediately and refreshed behind, so a longer `gcTime` makes the app
       * paint faster without ever showing something older than `staleTime`
       * allows.
       */
      gcTime: 15 * 60_000,
      retry: (failureCount, error) => {
        // Don't retry 4xx errors — they're deterministic failures
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
